import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { format, parseISO, isPast } from 'date-fns';
import { 
  Flame, Clock, CheckCircle, Users, ChevronLeft, Calendar, 
  Upload, Camera, Video, Flag, Trash2, Loader2, AlertCircle,
  Play, User, ImageIcon, Heart
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { PresignedImage, LazyVideo } from '../components/PresignedMedia';
import { uploadWithPresignedUrl } from '../utils/presignedUrls';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SidequestDetailPage() {
  const { id } = useParams();
  const { user, isAuthenticated } = useAuth();
  const [sidequest, setSidequest] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [caption, setCaption] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [reportReason, setReportReason] = useState('');
  const [likingSubmission, setLikingSubmission] = useState(null);
  const fileInputRef = useRef(null);
  
  // Thumbnail selection state for videos
  const [videoPreviewUrl, setVideoPreviewUrl] = useState(null);
  const [videoDuration, setVideoDuration] = useState(0);
  const [thumbnailTime, setThumbnailTime] = useState(0);
  const [thumbnailBlob, setThumbnailBlob] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const [videoReady, setVideoReady] = useState(false);
  const [loadingFrames, setLoadingFrames] = useState(false);
  const videoPreviewRef = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const [questRes, subRes] = await Promise.all([
        axios.get(`${API}/sidequests/${id}`),
        axios.get(`${API}/sidequests/${id}/submissions`)
      ]);
      setSidequest(questRes.data);
      setSubmissions(subRes.data.submissions);
    } catch (error) {
      console.error('Failed to fetch sidequest:', error);
      toast.error('Failed to load sidequest');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const isVideo = file.type.startsWith('video/');
    const isPhoto = file.type.startsWith('image/');
    
    if (!isVideo && !isPhoto) {
      toast.error('Please select an image or video file');
      return;
    }

    if (isVideo && !sidequest.allow_videos) {
      toast.error('Videos are not allowed for this sidequest');
      return;
    }

    if (isPhoto && !sidequest.allow_photos) {
      toast.error('Photos are not allowed for this sidequest');
      return;
    }

    const sizeMB = file.size / (1024 * 1024);
    if (isVideo && sizeMB > 100) {
      toast.error('Video must be under 100MB');
      return;
    }
    if (isPhoto && sizeMB > 10) {
      toast.error('Photo must be under 10MB');
      return;
    }

    // Reset thumbnail state
    setThumbnailBlob(null);
    setThumbnailPreview(null);
    setThumbnailTime(0);
    setVideoDuration(0);
    setVideoReady(false);
    setLoadingFrames(false);
    
    // Create object URL for video preview
    if (isVideo) {
      const url = URL.createObjectURL(file);
      setVideoPreviewUrl(url);
    } else {
      setVideoPreviewUrl(null);
    }

    setSelectedFile(file);
    setUploadModalOpen(true);
  };

  // Handle video metadata loaded - get duration
  const handleVideoLoaded = useCallback(() => {
    const video = videoPreviewRef.current;
    if (!video) return;
    
    setVideoDuration(video.duration);
  }, []);

  // Handle when video data is actually loaded and playable (needed for mobile)
  const handleVideoCanPlay = useCallback(() => {
    const video = videoPreviewRef.current;
    if (!video || videoReady) return;
    
    setVideoReady(true);
    // On mobile, we need to play briefly to load frames
    video.currentTime = 0.1; // Small offset to ensure frame loads
  }, [videoReady]);

  // Capture current frame as thumbnail
  const captureFrame = useCallback(() => {
    const video = videoPreviewRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    
    // Check if video has actual dimensions (frames loaded)
    if (video.videoWidth === 0 || video.videoHeight === 0) {
      console.log('Video dimensions not ready yet');
      return;
    }

    const ctx = canvas.getContext('2d');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    
    // Check if frame is not all black (mobile issue detection)
    const imageData = ctx.getImageData(0, 0, Math.min(10, canvas.width), Math.min(10, canvas.height));
    const isBlack = imageData.data.every((val, idx) => idx % 4 === 3 || val < 5);
    
    if (isBlack) {
      console.log('Frame appears black, video may not be ready');
      return;
    }
    
    // Convert to blob
    canvas.toBlob((blob) => {
      if (blob) {
        setThumbnailBlob(blob);
        setThumbnailPreview(URL.createObjectURL(blob));
        setLoadingFrames(false);
      }
    }, 'image/jpeg', 0.85);
  }, []);

  // Handle video seeked - capture frame when user scrubs
  const handleVideoSeeked = useCallback(() => {
    // Small delay to ensure frame is rendered (helps on mobile)
    setTimeout(() => {
      captureFrame();
    }, 100);
  }, [captureFrame]);

  // Handle slider change
  const handleThumbnailTimeChange = useCallback((value) => {
    const time = value[0];
    setThumbnailTime(time);
    setLoadingFrames(true);
    if (videoPreviewRef.current) {
      videoPreviewRef.current.currentTime = time;
    }
  }, []);

  // Initialize video for mobile - play briefly to load frames
  const initializeVideoForMobile = useCallback(async () => {
    const video = videoPreviewRef.current;
    if (!video) return;
    
    setLoadingFrames(true);
    
    try {
      // Play briefly to force frame loading on mobile
      video.muted = true;
      await video.play();
      // Immediately pause
      video.pause();
      // Seek to start
      video.currentTime = 0.1;
    } catch (err) {
      console.log('Auto-play not allowed, frames may load on seek:', err);
      // Still try to seek
      video.currentTime = 0.1;
    }
  }, []);

  const handleSubmit = async () => {
    if (!selectedFile) {
      toast.error('Please select a file');
      return;
    }

    const isVideo = selectedFile.type.startsWith('video/');
    
    // For videos, ensure we have a thumbnail (auto-captured or user-selected)
    let finalThumbnail = thumbnailBlob;
    if (isVideo && !finalThumbnail && videoPreviewRef.current) {
      // Capture current frame if no thumbnail was set
      const video = videoPreviewRef.current;
      const canvas = canvasRef.current;
      if (canvas && video.videoWidth > 0) {
        const ctx = canvas.getContext('2d');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        finalThumbnail = await new Promise(resolve => {
          canvas.toBlob(resolve, 'image/jpeg', 0.85);
        });
      }
    }

    setSubmitting(true);
    setUploadProgress(0);
    
    try {
      // Use presigned URL for direct upload to R2
      await uploadWithPresignedUrl({
        fileType: 'sidequest',
        targetId: id,
        file: selectedFile,
        caption: caption,
        thumbnailBlob: finalThumbnail,
        onProgress: setUploadProgress
      });

      toast.success('Submission uploaded!');
      setUploadModalOpen(false);
      setSelectedFile(null);
      setCaption('');
      setUploadProgress(0);
      setThumbnailBlob(null);
      setThumbnailPreview(null);
      setVideoPreviewUrl(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit');
    } finally {
      setSubmitting(false);
      setUploadProgress(0);
    }
  };

  const handleReport = async () => {
    if (!reportReason.trim()) {
      toast.error('Please provide a reason');
      return;
    }

    try {
      await axios.post(`${API}/sidequests/submissions/${selectedSubmission.id}/report`, {
        reason: reportReason
      });
      toast.success('Submission reported for review');
      setReportModalOpen(false);
      setReportReason('');
      setSelectedSubmission(null);
    } catch (error) {
      toast.error('Failed to report submission');
    }
  };

  const handleDeleteSubmission = async (submissionId) => {
    if (!window.confirm('Delete your submission?')) return;
    
    try {
      await axios.delete(`${API}/sidequests/submissions/${submissionId}`);
      toast.success('Submission deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete submission');
    }
  };

  const handleLikeSubmission = async (submission) => {
    if (!isAuthenticated) {
      toast.error('Please log in to like submissions');
      return;
    }

    setLikingSubmission(submission.id);
    try {
      const response = await axios.post(`${API}/submissions/${submission.id}/like`);
      
      // Update the submission in local state
      setSubmissions(prev => prev.map(sub => {
        if (sub.id === submission.id) {
          return {
            ...sub,
            like_count: response.data.like_count,
            user_liked: response.data.liked
          };
        }
        return sub;
      }));
    } catch (error) {
      toast.error('Failed to update like');
    } finally {
      setLikingSubmission(null);
    }
  };

  const isDeadlinePassed = sidequest ? isPast(parseISO(sidequest.deadline)) : false;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-red-500" />
      </div>
    );
  }

  if (!sidequest) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6">
        <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
        <h2 className="text-xl font-bold text-white mb-2">Sidequest not found</h2>
        <Link to="/sidequests">
          <Button className="bg-red-500 hover:bg-red-600 mt-4">Back to Sidequestz</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] px-4 sm:px-6 py-8">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <Link to="/sidequests" className="inline-flex items-center gap-2 text-neutral-400 hover:text-white mb-6">
          <ChevronLeft className="w-5 h-5" />
          Back to Sidequestz
        </Link>

        {/* Quest Header */}
        <div className="bg-[#121212] rounded-2xl border border-white/10 p-6 mb-8">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div className="flex items-center gap-2">
              {sidequest.user_submitted ? (
                <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Completed
                </Badge>
              ) : isDeadlinePassed ? (
                <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30">
                  <Clock className="w-3 h-3 mr-1" />
                  Late Submission Open
                </Badge>
              ) : (
                <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
                  <Flame className="w-3 h-3 mr-1" />
                  Active
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2 text-sm text-neutral-500">
              {sidequest.allow_photos && (
                <span className="flex items-center gap-1">
                  <Camera className="w-4 h-4" /> Photos
                </span>
              )}
              {sidequest.allow_videos && (
                <span className="flex items-center gap-1">
                  <Video className="w-4 h-4" /> Videos ({sidequest.max_video_length_seconds}s max)
                </span>
              )}
            </div>
          </div>

          <h1 className="text-3xl font-bold text-white mb-4">{sidequest.title}</h1>
          <p className="text-neutral-300 mb-6">{sidequest.description}</p>

          <div className="flex flex-wrap gap-4 text-sm text-neutral-400">
            <span className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-red-500" />
              Released: {format(parseISO(sidequest.release_date), 'MMM d, yyyy h:mm a')}
            </span>
            <span className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-orange-500" />
              Deadline: {format(parseISO(sidequest.deadline), 'MMM d, yyyy h:mm a')}
            </span>
            <span className="flex items-center gap-2">
              <Users className="w-4 h-4 text-green-500" />
              {sidequest.submission_count} submissions
            </span>
          </div>

          {/* Submit Button */}
          {isAuthenticated && !sidequest.user_submitted && (
            <div className="mt-6 pt-6 border-t border-white/10">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept="image/*,video/*"
                className="hidden"
              />
              <Button 
                onClick={() => fileInputRef.current?.click()}
                className="bg-orange-500 hover:bg-orange-600 rounded-full"
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload Submission
                {isDeadlinePassed && <span className="ml-2 text-xs opacity-75">(Late)</span>}
              </Button>
            </div>
          )}

          {!isAuthenticated && (
            <div className="mt-6 pt-6 border-t border-white/10">
              <Link to="/login">
                <Button className="bg-red-500 hover:bg-red-600 rounded-full">
                  Sign in to Participate
                </Button>
              </Link>
            </div>
          )}
        </div>

        {/* Submissions */}
        <h2 className="text-xl font-bold text-white mb-4">Submissions ({submissions.length})</h2>
        
        {submissions.length === 0 ? (
          <div className="text-center py-12 bg-[#121212] rounded-2xl border border-white/10">
            <Camera className="w-12 h-12 text-neutral-600 mx-auto mb-4" />
            <p className="text-neutral-400">No submissions yet. Be the first!</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4">
            {submissions.map(sub => (
              <div key={sub.id} className="bg-[#121212] rounded-2xl border border-white/10 overflow-hidden">
                {/* Media */}
                <div className="relative aspect-video bg-black">
                  {sub.media_type === 'video' ? (
                    <LazyVideo 
                      src={sub.media_urls[0]}
                      thumbnailSrc={sub.thumbnail_url || sub.media_urls[0]}
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <PresignedImage 
                      src={sub.media_urls[0]}
                      alt="Submission"
                      className="w-full h-full object-contain"
                    />
                  )}
                  {sub.is_late && (
                    <Badge className="absolute top-2 left-2 bg-orange-500/80 text-white z-10">
                      Submitted Late
                    </Badge>
                  )}
                </div>

                {/* Info */}
                <div className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <Link 
                      to={`/u/${sub.anonymous_username}`}
                      className="flex items-center gap-2 text-white hover:text-red-400"
                    >
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center">
                        <User className="w-4 h-4 text-white" />
                      </div>
                      <span className="font-medium">{sub.anonymous_username}</span>
                    </Link>
                    <span className="text-xs text-neutral-500">
                      {format(parseISO(sub.submitted_at), 'MMM d')}
                    </span>
                  </div>

                  {sub.caption && (
                    <p className="text-neutral-300 text-sm mb-3">{sub.caption}</p>
                  )}

                  <div className="flex items-center justify-between">
                    {/* Like Button */}
                    <button
                      onClick={() => handleLikeSubmission(sub)}
                      disabled={likingSubmission === sub.id}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-all ${
                        sub.user_liked 
                          ? 'bg-red-500/20 text-red-400' 
                          : 'bg-white/5 text-neutral-400 hover:bg-white/10 hover:text-white'
                      }`}
                    >
                      {likingSubmission === sub.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Heart className={`w-4 h-4 ${sub.user_liked ? 'fill-red-400' : ''}`} />
                      )}
                      <span className="text-sm font-medium">{sub.like_count || 0}</span>
                    </button>

                    <div className="flex items-center gap-2">
                      {isAuthenticated && sub.user_id === user?.id && (
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleDeleteSubmission(sub.id)}
                          className="text-red-400 hover:bg-red-500/10"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                      {isAuthenticated && sub.user_id !== user?.id && (
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => { setSelectedSubmission(sub); setReportModalOpen(true); }}
                          className="text-neutral-400 hover:bg-white/10"
                        >
                          <Flag className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Upload Modal */}
      <Dialog open={uploadModalOpen} onOpenChange={setUploadModalOpen}>
        <DialogContent className="bg-[#121212] border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">Submit Your Entry</DialogTitle>
            <DialogDescription className="text-neutral-400">
              {selectedFile?.name}
              {isDeadlinePassed && (
                <span className="block text-orange-400 mt-1">
                  This will be marked as a late submission
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {/* Hidden canvas for thumbnail capture */}
            <canvas ref={canvasRef} className="hidden" />
            
            {selectedFile && (
              <>
                {selectedFile.type.startsWith('video/') ? (
                  <>
                    {/* Video preview with thumbnail selector */}
                    <div className="space-y-3">
                      <div className="rounded-xl overflow-hidden bg-black aspect-video relative">
                        <video 
                          ref={videoPreviewRef}
                          src={videoPreviewUrl}
                          className="w-full h-full object-contain"
                          onLoadedMetadata={handleVideoLoaded}
                          onCanPlay={handleVideoCanPlay}
                          onSeeked={handleVideoSeeked}
                          muted
                          playsInline
                          preload="auto"
                          crossOrigin="anonymous"
                        />
                        {/* Mobile initialization overlay */}
                        {videoDuration > 0 && !thumbnailPreview && (
                          <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                            <button
                              onClick={initializeVideoForMobile}
                              className="flex flex-col items-center gap-2 text-white"
                            >
                              {loadingFrames ? (
                                <Loader2 className="w-8 h-8 animate-spin" />
                              ) : (
                                <>
                                  <div className="w-12 h-12 rounded-full bg-orange-500 flex items-center justify-center">
                                    <Play className="w-6 h-6 ml-0.5" />
                                  </div>
                                  <span className="text-sm">Tap to load frames</span>
                                </>
                              )}
                            </button>
                          </div>
                        )}
                      </div>
                      
                      {/* Thumbnail selection controls */}
                      {videoDuration > 0 && (
                        <div className="bg-neutral-900 rounded-lg p-4 space-y-3">
                          <div className="flex items-center gap-2 text-sm">
                            <ImageIcon className="w-4 h-4 text-orange-500" />
                            <span className="text-white font-medium">Select Thumbnail</span>
                            <span className="text-neutral-500 text-xs ml-auto">
                              Drag to pick a frame
                            </span>
                          </div>
                          
                          <Slider
                            value={[thumbnailTime]}
                            onValueChange={handleThumbnailTimeChange}
                            max={videoDuration}
                            step={0.1}
                            className="w-full"
                          />
                          
                          <div className="flex items-center justify-between text-xs text-neutral-500">
                            <span>0:00</span>
                            <span className="text-orange-400">
                              {Math.floor(thumbnailTime / 60)}:{String(Math.floor(thumbnailTime % 60)).padStart(2, '0')}
                            </span>
                            <span>
                              {Math.floor(videoDuration / 60)}:{String(Math.floor(videoDuration % 60)).padStart(2, '0')}
                            </span>
                          </div>
                          
                          {/* Thumbnail preview */}
                          {(thumbnailPreview || loadingFrames) && (
                            <div className="flex items-center gap-3 pt-2 border-t border-white/10">
                              <div className="w-20 h-12 rounded overflow-hidden bg-black flex-shrink-0 flex items-center justify-center">
                                {loadingFrames ? (
                                  <Loader2 className="w-4 h-4 animate-spin text-neutral-500" />
                                ) : thumbnailPreview ? (
                                  <img 
                                    src={thumbnailPreview} 
                                    alt="Thumbnail preview"
                                    className="w-full h-full object-cover"
                                  />
                                ) : null}
                              </div>
                              <span className="text-xs text-neutral-400">
                                {loadingFrames ? 'Loading frame...' : 'This frame will be used as the thumbnail'}
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="rounded-xl overflow-hidden bg-black aspect-video">
                    <img 
                      src={URL.createObjectURL(selectedFile)}
                      alt="Preview"
                      className="w-full h-full object-contain"
                    />
                  </div>
                )}
              </>
            )}

            <div>
              <Label className="text-white">Caption (optional)</Label>
              <Textarea 
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                placeholder="Add a caption..."
                className="bg-neutral-900 border-white/10 text-white mt-2"
                rows={3}
              />
            </div>

            {submitting && uploadProgress > 0 && (
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-neutral-400">Uploading...</span>
                  <span className="text-white">{uploadProgress}%</span>
                </div>
                <div className="h-2 bg-neutral-800 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-orange-500 transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>
            )}

            <Button 
              onClick={handleSubmit}
              disabled={submitting}
              className="w-full bg-orange-500 hover:bg-orange-600 rounded-full"
            >
              {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Submit'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Report Modal */}
      <Dialog open={reportModalOpen} onOpenChange={setReportModalOpen}>
        <DialogContent className="bg-[#121212] border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Report Submission</DialogTitle>
            <DialogDescription className="text-neutral-400">
              Please provide a reason for reporting this submission
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <Textarea 
              value={reportReason}
              onChange={(e) => setReportReason(e.target.value)}
              placeholder="Reason for report..."
              className="bg-neutral-900 border-white/10 text-white"
              rows={3}
            />

            <Button 
              onClick={handleReport}
              className="w-full bg-red-500 hover:bg-red-600 rounded-full"
            >
              Submit Report
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
